import { useEffect, useRef, useState } from "react";
import { MessageSquare, X, Send } from "lucide-react";
import { API } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

function getSessionId() {
  let id = localStorage.getItem("ltc_chat_session");
  if (!id) {
    id = "sess_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem("ltc_chat_session", id);
  }
  return id;
}

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hey — I'm Nova, LeadTech Connect's AI assistant. Ask me about our services, or how we can help build your pipeline." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), message: text }),
      });
      if (!res.ok || !res.body) throw new Error("Chat unavailable");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const p of parts) {
          if (!p.startsWith("data:")) continue;
          const data = p.slice(5).trimStart();
          if (data === "[DONE]") continue;
          if (data.startsWith("[error")) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: "Sorry, I ran into an issue. Please try again." };
              return copy;
            });
            continue;
          }
          setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = { role: "assistant", content: (copy[copy.length - 1].content || "") + data };
            return copy;
          });
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "The chat service is unavailable right now. Please use the contact form." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          data-testid="chatbot-open"
          aria-label="Open chatbot"
          onClick={() => { setOpen(true); trackEvent("chatbot_open"); }}
          className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-[#007AFF] text-white flex items-center justify-center shadow-[0_10px_30px_rgba(0,122,255,0.4)] hover:shadow-[0_16px_40px_rgba(8,145,178,0.5)] hover:bg-[#0056d3] transition-shadow"
        >
          <MessageSquare size={22} />
        </button>
      )}

      {open && (
        <div
          data-testid="chatbot-window"
          className="fixed bottom-6 right-6 z-40 w-[92vw] max-w-[380px] h-[520px] bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 shadow-2xl flex flex-col"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-[#0891B2] animate-pulse" aria-hidden="true" />
              <span className="font-heading font-bold text-sm text-slate-900 dark:text-white">Nova · AI Assistant</span>
            </div>
            <button
              data-testid="chatbot-close"
              aria-label="Close chatbot"
              onClick={() => setOpen(false)}
              className="text-slate-500 dark:text-slate-500 hover:text-slate-900 p-1"
            >
              <X size={16} />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-white dark:bg-slate-950">
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`chat-msg-${m.role}`}
                className={`text-sm leading-relaxed ${
                  m.role === "user"
                    ? "ml-auto bg-[#007AFF] text-white px-3 py-2 max-w-[85%]"
                    : "mr-auto bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-100 border border-slate-200 dark:border-slate-800 px-3 py-2 max-w-[85%]"
                }`}
              >
                {m.content || <span className="text-slate-400 dark:text-slate-500">…</span>}
              </div>
            ))}
          </div>

          <div className="border-t border-slate-200 dark:border-slate-800 p-3 flex gap-2 bg-white dark:bg-slate-950">
            <input
              data-testid="chatbot-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
              placeholder="Ask about our services…"
              className="flex-1 bg-white dark:bg-slate-950 text-slate-900 dark:text-white text-sm border border-slate-300 dark:border-slate-700 focus:border-[#007AFF] outline-none px-3 py-2"
              disabled={busy}
            />
            <button
              data-testid="chatbot-send"
              aria-label="Send"
              onClick={send}
              disabled={busy}
              className="bg-[#007AFF] hover:bg-[#0056d3] disabled:opacity-50 text-white px-3 flex items-center justify-center"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
